import { useCallback, useEffect, useState } from 'react';
import { type Partner, type Tender } from '../data/commercial.config';
import { BACKEND } from '../lib/db';
import { fetchPartnersLive, fetchTendersLive, recordDueDiligence, type DdForm } from '../lib/channelLive';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';

/* FROM THE TENANT, NOT FROM SCIRA'S SEED.

   These were imported straight out of data/commercial.ts, which is one customer's partner and
   tender list. Vite bundles modules rather than symbols, so naming them here pulled that whole
   file into every build, including opportunities against named hospitals. The demonstration
   builds happened to look clean because the bundler could fold the unused parts away; the
   showcase build, whose tenant is resolved at runtime, could not, and the isolation check
   caught a real customer's pipeline sitting in a public bundle.

   This is the third instance of the same mistake after the Atlas seed and the Ledger examples,
   which is why the rule is now blunt: a module under modules/ may not import from data/ for
   anything except types and configuration. */
const SEED_PARTNERS = TENANT.seed.partner as Partner[];
const SEED_TENDERS = TENANT.seed.tender as Tender[];
/* The seed's tender countdowns are written against the demonstration's fixed day; live
   data counts from the real one. */
const TODAY = BACKEND === 'supabase' ? new Date() : new Date('2026-08-02');
import './pipeline.css';
import { NewPartner, NewTerritory, NewTender } from '../components/CreateForms';

/* =====================================================================================
   Channel.

   The module that exists because in Saudi Arabia and the UAE you legally cannot sell
   direct, under EU IVDR the distributor is a defined economic operator, and under the UK
   Bribery Act 2010 section 7 the company is liable for bribery by an associated person
   unless it can show adequate procedures.

   The due diligence panel is deliberately prominent and deliberately obstructive. An
   order cannot be raised against a partner whose diligence has lapsed, enforced by a
   database trigger rather than by a policy anyone can talk their way past.
   ===================================================================================== */

const fmtMoney = (n: number, ccy: string) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(n);

function ddState(p: Partner): { cls: string; label: string; blocking: boolean } {
  const d = p.dueDiligence;
  if (d.status === 'approved' && d.riskRating !== 'unacceptable') {
    return { cls: 'ok', label: 'Cleared', blocking: false };
  }
  if (d.status === 'in_progress') return { cls: 'pending', label: 'In progress', blocking: true };
  if (d.status === 'expired') return { cls: 'blocked', label: 'Expired', blocking: true };
  if (d.status === 'rejected') return { cls: 'blocked', label: 'Rejected', blocking: true };
  return { cls: 'blocked', label: 'Not started', blocking: true };
}

export function ChannelView() {
  const [create, setCreate] = useState<'partner' | 'territory' | 'tender' | null>(null);
  const [tab, setTab] = useState<'partners' | 'territories' | 'tenders'>('partners');
  const [sel, setSel] = useState<Partner | null>(null);
  const [partners, setPartners] = useState<Partner[]>(SEED_PARTNERS);
  const [tenders, setTenders] = useState<Tender[]>(SEED_TENDERS);

  const load = useCallback(() => {
    if (BACKEND !== 'supabase') return;
    fetchPartnersLive().then(setPartners).catch(onFail);
    fetchTendersLive().then(setTenders).catch(onFail);
  }, []);
  useEffect(() => { load(); }, [load]);

  const blocked = partners.filter((p) => ddState(p).blocking);

  return (
    <div className="page channel">
      <div className="page-head">
        <h1 className="page-title">Channel</h1>
        <p className="page-sub">
          Distributors, agents and territory grants. In the Gulf you cannot sell direct, and under
          EU IVDR a distributor is a regulated economic operator, so this is a compliance module as
          much as a commercial one.
        </p>
      </div>

      {partners.length === 0 && (
        <p className="module-empty">No distribution partners yet. Add the company first, then grant it a territory; the database refuses two overlapping exclusives for the same country.</p>
      )}

      <div className="toolbar">
        <div className="seg">
          {(['partners', 'territories', 'tenders'] as const).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t === 'partners' ? 'Partners' : t === 'territories' ? 'Territories' : 'Tenders'}
            </button>
          ))}
        </div>
        <button className="btn" style={{ marginLeft: 'auto' }}
                onClick={() => setCreate('territory')}>Grant territory</button>
        <button className="btn" onClick={() => setCreate('tender')}>Add tender</button>
        <button className="btn primary" onClick={() => setCreate('partner')}>Add partner</button>
      </div>

      {blocked.length > 0 && (
        <div className="insight card" style={{ borderLeftColor: 'var(--risk)', background: 'var(--risk-soft)' }}>
          <strong>
            {blocked.length} partner{blocked.length === 1 ? '' : 's'} cannot be ordered against.
          </strong>
          <span>
            Anti-bribery due diligence is absent, incomplete or lapsed. Order entry is blocked by the
            database, not by policy. This is the UK Bribery Act section 7 adequate-procedures defence
            made operational, and it is meant to be inconvenient.
          </span>
        </div>
      )}

      {tab === 'partners' && (
        <div className="partner-grid">
          {partners.map((p) => {
            const dd = ddState(p);
            return (
              <article key={p.id} className={`card partner-card ${dd.cls}`}>
                <header className="partner-head">
                  <div>
                    <div className="partner-name">{p.name}</div>
                    <div className="partner-sub">
                      {p.country} · {p.type.replace(/_/g, ' ')}
                      {p.regulatoryRole && ` · ${p.regulatoryRole}`}
                    </div>
                  </div>
                  <span className={`pill ${p.status === 'active' ? 'ok' : p.status === 'contracted' ? 'info' : 'mute'}`}>
                    {p.status.replace('_', ' ')}
                  </span>
                </header>

                <div className={`dd-block ${dd.blocking ? 'fail' : ''}`}>
                  <div className="dd-head">
                    <span className="dd-title">Anti-bribery due diligence</span>
                    <span className={`pill ${dd.blocking ? 'risk' : 'ok'}`}>{dd.label}</span>
                  </div>
                  <div className="dd-checks">
                    <Check ok={p.dueDiligence.sanctionsScreened} label="Sanctions screening" />
                    <Check ok={p.dueDiligence.pepScreened} label="Politically exposed persons" />
                    <Check ok={p.dueDiligence.abacSigned} label="Compliance certification signed" />
                  </div>
                  {p.dueDiligence.nextReview && (
                    <div className="partner-sub" style={{ marginTop: 7 }}>
                      Next review {p.dueDiligence.nextReview}
                      {p.dueDiligence.riskRating && ` · risk ${p.dueDiligence.riskRating}`}
                    </div>
                  )}
                  {dd.blocking && (
                    <div className="partner-sub" style={{ marginTop: 6, color: 'var(--risk)' }}>
                      Orders blocked until approved.
                    </div>
                  )}
                </div>

                {p.territories.length > 0 && (
                  <div>
                    <div className="label" style={{ marginBottom: 6 }}>Territory grants</div>
                    <div className="terr-list">
                      {p.territories.map((t) => (
                        <div key={t.country + t.product} className="terr">
                          <span className="terr-country">{t.country}</span>
                          <span className="terr-product">{t.product}</span>
                          <span className={`pill ${t.exclusivity === 'exclusive' ? 'accent' : 'mute'}`}>
                            {t.exclusivity.replace('_', ' ')}
                          </span>
                          <span className="terr-until">to {t.until}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {p.commitmentValue != null && (
                  <div className="commit">
                    <div className="commit-row">
                      <span>Minimum commitment</span>
                      <span>
                        {p.achievedPct}% of {fmtMoney(p.commitmentValue, p.commitmentCurrency ?? 'EUR')}
                      </span>
                    </div>
                    <div className="bar">
                      <span
                        className={(p.achievedPct ?? 0) >= 100 ? 'over' : 'under'}
                        style={{ width: `${Math.min(p.achievedPct ?? 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {p.sellThroughStatus && (
                  <div className="partner-sub">
                    Sell-through report {p.sellThroughStatus}
                    {p.sellThroughDue && `, due ${p.sellThroughDue}`}
                  </div>
                )}

                {p.note && <p className="drawer-text" style={{ fontSize: 'var(--t-xs)' }}>{p.note}</p>}

                <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => setSel(p)}>
                  Open
                </button>
              </article>
            );
          })}
        </div>
      )}

      {tab === 'territories' && (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Country</th><th>Product</th><th>Partner</th>
                <th>Exclusivity</th><th>Expires</th><th>Conflict check</th>
              </tr>
            </thead>
            <tbody>
              {partners.flatMap((p) =>
                p.territories.map((t) => (
                  <tr key={p.id + t.country + t.product}>
                    <td className="mono">{t.country}</td>
                    <td>{t.product}</td>
                    <td>{p.name}</td>
                    <td>
                      <span className={`pill ${t.exclusivity === 'exclusive' ? 'accent' : 'mute'}`}>
                        {t.exclusivity.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="num">{t.until}</td>
                    <td><span className="pill ok">no overlap</span></td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          <div className="table-note">
            Overlapping exclusive grants on the same country and product are prevented by a database
            exclusion constraint, not by this check. The constraint is what stops the mistake that
            produces litigation; this column merely reports it.
          </div>
        </div>
      )}

      {tab === 'tenders' && (
        <div className="tender-list">
          {tenders.map((t) => {
            const days = Math.round(
              (new Date(t.submissionDeadline).getTime() - TODAY.getTime()) / 86_400_000,
            );
            return (
              <article key={t.id} className={`card tender ${days <= 45 ? 'urgent' : ''}`}>
                <div className="tender-when">
                  <div className="entry-days">{days}</div>
                  <div className="entry-days-l">days</div>
                </div>
                <div className="entry-main">
                  <header className="entry-head">
                    <h3>{t.title}</h3>
                    <span className={`pill ${t.bidDecision === 'bid' ? 'ok' : t.bidDecision === 'no_bid' ? 'risk' : 'mute'}`}>
                      {t.bidDecision.replace('_', ' ')}
                    </span>
                  </header>
                  <div className="partner-sub">
                    {t.authority} · {t.country} · <span className="mono">{t.ref}</span>
                  </div>
                  {t.note && <p className="entry-detail">{t.note}</p>}
                  <footer className="entry-foot">
                    <span className="pill accent">{fmtMoney(t.estimatedValue, t.currency)}</span>
                    <span className="pill mute">{t.status.replace('_', ' ')}</span>
                    {t.contractMonths && <span>{t.contractMonths} month contract</span>}
                    <span className="entry-date">closes {t.submissionDeadline}</span>
                  </footer>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {sel && <PartnerDrawer p={sel} onClose={() => setSel(null)}
                    onSaved={() => { setSel(null); load(); }} />}
      {create === 'partner' && (
        <NewPartner onClose={() => setCreate(null)} onDone={() => { setCreate(null); load(); }} />
      )}
      {create === 'territory' && (
        <NewTerritory partners={partners.map((p) => ({ id: p.id, name: p.name }))}
                      onClose={() => setCreate(null)} onDone={() => { setCreate(null); load(); }} />
      )}
      {create === 'tender' && (
        <NewTender partners={partners.map((p) => ({ id: p.id, name: p.name }))}
                   onClose={() => setCreate(null)} onDone={() => { setCreate(null); load(); }} />
      )}

    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="dd-check">
      <span className={`dd-mark ${ok ? 'yes' : 'no'}`}>{ok ? '✓' : '✕'}</span>
      {label}
    </div>
  );
}

/* Twelve months is the default review cycle: annual refresh is the convention the
   adequate-procedures guidance expects, and the gate goes red the day it lapses. */
function plusTwelveMonths(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 12);
  return d.toISOString().slice(0, 10);
}

function PartnerDrawer({ p, onClose, onSaved }: {
  p: Partner; onClose: () => void; onSaved: () => void;
}) {
  const dd = ddState(p);
  const [f, setF] = useState<DdForm>({
    sanctionsScreened: false, sanctionsResult: '',
    pepScreened: false, pepResult: '',
    adverseMediaChecked: false, adverseMediaResult: '',
    beneficialOwnershipVerified: false, governmentOfficialLinks: '',
    abacSigned: false, trainingCompleted: false,
    riskRating: '', conditions: '', nextReviewDue: plusTwelveMonths(), notes: '',
    approved: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof DdForm) => (v: boolean | string) => setF((x) => ({ ...x, [k]: v }));
  const checksDone = f.sanctionsScreened && f.pepScreened && f.abacSigned;
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer card" role="dialog" aria-label={p.name}>
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow">{p.country} · {p.type.replace(/_/g, ' ')}</div>
            <h2 className="drawer-title display">{p.name}</h2>
            <div className="drawer-tags">
              <span className={`pill ${p.status === 'active' ? 'ok' : 'mute'}`}>{p.status.replace('_', ' ')}</span>
              <span className={`pill ${dd.blocking ? 'risk' : 'ok'}`}>Diligence: {dd.label}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="drawer-body">
          {dd.blocking && (
            <section>
              <div className="blocked-box">
                <strong>Order entry is blocked for this partner.</strong>
                <p>
                  The database will reject an order line referencing this partner until an approved,
                  in-date due diligence record exists. Record the assessment below; approving it
                  unblocks ordering and activates the partner.
                </p>
              </div>
            </section>
          )}

          {BACKEND === 'supabase' && dd.blocking && (
            <section>
              <div className="label">Record the due diligence</div>
              {err && <p className="block-note">{err}</p>}
              <label className="lv-check"><input type="checkbox" checked={f.sanctionsScreened}
                onChange={(e) => set('sanctionsScreened')(e.target.checked)} />
                <span>Sanctions screening done (OFSI consolidated list, OFAC, EU, UN)</span></label>
              {f.sanctionsScreened && (
                <input className="input" placeholder="Result, e.g. no matches, screened 22 Oct 2026"
                       value={f.sanctionsResult} onChange={(e) => set('sanctionsResult')(e.target.value)} />)}
              <label className="lv-check"><input type="checkbox" checked={f.pepScreened}
                onChange={(e) => set('pepScreened')(e.target.checked)} />
                <span>Politically exposed persons checked</span></label>
              {f.pepScreened && (
                <input className="input" placeholder="Result"
                       value={f.pepResult} onChange={(e) => set('pepResult')(e.target.value)} />)}
              <label className="lv-check"><input type="checkbox" checked={f.adverseMediaChecked}
                onChange={(e) => set('adverseMediaChecked')(e.target.checked)} />
                <span>Adverse media searched</span></label>
              {f.adverseMediaChecked && (
                <input className="input" placeholder="Result"
                       value={f.adverseMediaResult} onChange={(e) => set('adverseMediaResult')(e.target.value)} />)}
              <label className="lv-check"><input type="checkbox" checked={f.beneficialOwnershipVerified}
                onChange={(e) => set('beneficialOwnershipVerified')(e.target.checked)} />
                <span>Beneficial ownership verified against the company register</span></label>
              <input className="input" placeholder="Links to government officials, if any"
                     value={f.governmentOfficialLinks}
                     onChange={(e) => set('governmentOfficialLinks')(e.target.value)} />
              <label className="lv-check"><input type="checkbox" checked={f.abacSigned}
                onChange={(e) => set('abacSigned')(e.target.checked)} />
                <span>Anti-bribery certification signed by the partner</span></label>
              <label className="lv-check"><input type="checkbox" checked={f.trainingCompleted}
                onChange={(e) => set('trainingCompleted')(e.target.checked)} />
                <span>Anti-bribery training completed</span></label>
              <div className="cf-row">
                <select className="input sel" value={f.riskRating}
                        onChange={(e) => set('riskRating')(e.target.value)}>
                  <option value="">Risk rating…</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="unacceptable">Unacceptable</option>
                </select>
                <input className="input" type="date" value={f.nextReviewDue}
                       title="Next review due"
                       onChange={(e) => set('nextReviewDue')(e.target.value)} />
              </div>
              <input className="input" placeholder="Conditions, if approval is conditional"
                     value={f.conditions} onChange={(e) => set('conditions')(e.target.value)} />
              <input className="input" placeholder="Notes"
                     value={f.notes} onChange={(e) => set('notes')(e.target.value)} />
              <label className="lv-check"><input type="checkbox" checked={f.approved}
                disabled={!checksDone}
                onChange={(e) => set('approved')(e.target.checked)} />
                <span>Approve{checksDone ? '' : ' (sanctions, PEP and the signed certification first)'}</span></label>
              <button className="btn primary" disabled={busy || !f.riskRating} onClick={async () => {
                setBusy(true); setErr(null);
                try {
                  await recordDueDiligence(p.id, f);
                  onSaved();
                } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
              }}>{f.approved ? 'Save and approve' : 'Save assessment'}</button>
            </section>
          )}

          {p.regulatoryRole && (
            <section>
              <div className="label">Regulatory role</div>
              <p className="drawer-text">{p.regulatoryRole}</p>
              <div className="drawer-note">
                Under EU IVDR the importer and distributor are defined economic operators and the
                manufacturer must be able to identify every operator it supplied. In Saudi Arabia and
                the UAE, market authorisation is held in the agent's name and is difficult to move,
                so this appointment is close to irreversible.
              </div>
            </section>
          )}

          {p.territories.length > 0 && (
            <section>
              <div className="label">Territory grants</div>
              <div className="terr-list">
                {p.territories.map((t) => (
                  <div key={t.country + t.product} className="terr">
                    <span className="terr-country">{t.country}</span>
                    <span className="terr-product">{t.product}</span>
                    <span className={`pill ${t.exclusivity === 'exclusive' ? 'accent' : 'mute'}`}>
                      {t.exclusivity.replace('_', ' ')}
                    </span>
                    <span className="terr-until">to {t.until}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {p.commitmentValue != null && (
            <section>
              <div className="label">Performance against commitment</div>
              <div className="big-number">{p.achievedPct}%</div>
              <div className="drawer-note">
                of {fmtMoney(p.commitmentValue, p.commitmentCurrency ?? 'EUR')} minimum.
                {(p.achievedPct ?? 0) < 100
                  ? ' Falling short is the contractual trigger for converting exclusivity to non-exclusive, so track it before the anniversary rather than after.'
                  : ' Exceeding commitment. Worth reviewing whether the territory should be extended.'}
              </div>
            </section>
          )}

          {p.note && (
            <section>
              <div className="label">Notes</div>
              <p className="drawer-text">{p.note}</p>
            </section>
          )}

          <section>
            <div className="label">Actions</div>
            <div className="drawer-actions">
              <button className="btn primary" disabled={dd.blocking}>Raise order</button>
              <button className="btn" disabled
        title="Diligence updates are not built yet. Record the review in Quality.">
  Update diligence
</button>
              <button className="btn" disabled
        title="Stock report requests are not built yet. Ask the distributor through Chorus.">
  Request stock report
</button>
            </div>
            {dd.blocking && (
              <div className="drawer-note">Order entry is disabled until diligence is approved.</div>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}
